<template>
  <span
    v-if="tooltipText"
    class="column-field-help"
    @click.stop
    @mousedown.stop
    @mouseenter="openPopover"
    @mouseleave="closePopover"
    @focusin="openPopover"
    @focusout="onFocusOut"
  >
    <button
      ref="trigger"
      type="button"
      class="btn btn-sm column-field-help-btn"
      :aria-label="ariaLabel"
    >
      <i class="fas fa-question-circle" aria-hidden="true"></i>
    </button>
    <span
      v-show="popoverVisible"
      ref="popover"
      class="column-field-help-popover"
      :class="{ 'column-field-help-popover--right': popoverOnRight }"
      :style="popoverStyle"
      role="tooltip"
      @mouseenter="openPopover"
      @mouseleave="closePopover"
    >{{ tooltipText }}</span>
  </span>
</template>

<script>
export default {
  name: "ColumnFieldHelpIcon",
  props: {
    tooltip: {
      type: String,
      default: "",
    },
    ariaLabel: {
      type: String,
      default: "",
    },
    popoverPreferRight: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      popoverVisible: false,
      popoverStyle: {},
      popoverOnRight: false,
    };
  },
  computed: {
    tooltipText() {
      const t = this.tooltip != null ? String(this.tooltip).trim() : "";
      return t;
    },
  },
  beforeDestroy() {
    clearTimeout(this._closeTimer);
    this.detachScrollListener();
    this.removePopoverFromBody();
  },
  methods: {
    openPopover() {
      clearTimeout(this._closeTimer);
      this.popoverVisible = true;
      this.$nextTick(() => {
        this.ensurePopoverInBody();
        this.updatePopoverPosition();
        this.attachScrollListener();
      });
    },
    closePopover() {
      clearTimeout(this._closeTimer);
      this._closeTimer = setTimeout(() => {
        this.popoverVisible = false;
        this.detachScrollListener();
      }, 80);
    },
    onFocusOut(event) {
      const root = this.$el;
      if (root && root.contains(event.relatedTarget)) return;
      this.closePopover();
    },
    attachScrollListener() {
      if (this._scrollListener) return;
      this._scrollListener = () => {
        if (this.popoverVisible) this.updatePopoverPosition();
      };
      window.addEventListener("scroll", this._scrollListener, true);
      window.addEventListener("resize", this._scrollListener);
    },
    detachScrollListener() {
      if (!this._scrollListener) return;
      window.removeEventListener("scroll", this._scrollListener, true);
      window.removeEventListener("resize", this._scrollListener);
      this._scrollListener = null;
    },
    ensurePopoverInBody() {
      const popover = this.$refs.popover;
      if (!popover || popover.parentNode === document.body) return;
      document.body.appendChild(popover);
    },
    removePopoverFromBody() {
      const popover = this.$refs.popover;
      if (!popover || popover.parentNode !== document.body) return;
      popover.parentNode.removeChild(popover);
    },
    updatePopoverPosition() {
      const trigger = this.$refs.trigger;
      if (!trigger) return;

      const rect = trigger.getBoundingClientRect();
      const gap = 14;
      const popoverEl = this.$refs.popover;
      const popoverWidth = popoverEl ? popoverEl.offsetWidth : 220;
      const fitsRight = rect.right + gap + popoverWidth <= window.innerWidth - 8;
      const fitsLeft = rect.left - gap - popoverWidth >= 8;
      const placeRight = this.popoverPreferRight
        ? fitsRight || !fitsLeft
        : !fitsLeft;

      this.popoverOnRight = placeRight;

      if (placeRight) {
        this.popoverStyle = {
          top: `${rect.top + rect.height / 2}px`,
          left: `${rect.right + gap}px`,
          transform: "translate(0, -50%)",
        };
      } else {
        this.popoverStyle = {
          top: `${rect.top + rect.height / 2}px`,
          left: `${rect.left - gap}px`,
          transform: "translate(-100%, -50%)",
        };
      }
    },
  },
};
</script>

<style scoped>
.column-field-help {
  position: relative;
  display: inline-flex;
  align-items: center;
  vertical-align: middle;
  flex-shrink: 0;
}

.column-field-help-btn {
  opacity: 0;
  transition: opacity 0.2s ease;
  padding: 0 2px;
  font-size: 0.85em;
  line-height: 1;
  border: none;
  background: transparent;
  color: rgba(255, 255, 255, 0.85);
}

.column-field-help-btn:hover,
.column-field-help-btn:focus {
  color: #fff;
  background: transparent;
  box-shadow: none;
}

.column-field-help-popover {
  position: fixed;
  z-index: 1080;
  min-width: 180px;
  max-width: 300px;
  padding: 8px 10px;
  font-size: 0.75rem;
  font-weight: normal;
  line-height: 1.35;
  text-align: left;
  white-space: normal;
  color: #212529;
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 6px;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.15);
}

.column-field-help-popover::after {
  content: "";
  position: absolute;
  top: 50%;
  left: 100%;
  transform: translateY(-50%);
  border: 6px solid transparent;
  border-left-color: #fff;
}

.column-field-help-popover--right::after {
  left: auto;
  right: 100%;
  border-left-color: transparent;
  border-right-color: #fff;
}
</style>
